// packages/web/src/pages/teach/CreateHomeworkPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHomeworkStore } from '../../stores/homeworkStore';
import { useExerciseLibraryStore } from '../../stores/exerciseLibraryStore';
import { useTeachStore } from '../../stores/teachStore';
import { useBasePath } from '../../contexts/BasePathContext';
import { useStore } from '../../stores';
import { ArrowLeft, Plus, X, Users, BookOpen } from 'lucide-react';
import { Toast } from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import { familyApi, FamilyChild } from '../../services/familyApi';
import { buildStaticUrl } from '../../config/env';

export const CreateHomeworkPage: React.FC = () => {
  const navigate = useNavigate();
  const { basePath } = useBasePath();
  const { createForm, setCreateFormField, createHomework, loading } = useHomeworkStore();
  const { exercises, fetchExercises } = useExerciseLibraryStore();
  const { classes, students, fetchClasses, fetchStudents } = useTeachStore();
  const { user } = useStore();
  const { toast, success, error, warning, hideToast } = useToast();

  const isParent = user?.role === 'parent';
  const [selectedExercises, setSelectedExercises] = useState<string[]>([]);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'class' | 'student'>('class');
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [familyChildren, setFamilyChildren] = useState<FamilyChild[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);

  useEffect(() => {
    fetchExercises();
    if (isParent) {
      familyApi.getOverview().then(data => {
        setFamilyChildren(data.children);
      }).catch(() => {});
    } else {
      fetchClasses();
      fetchStudents();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!createForm.name || !createForm.dueDate || selectedExercises.length === 0) {
      warning('请填写必填字段');
      return;
    }

    // 检查是否选择了布置对象
    if (isParent) {
      if (selectedChildren.length === 0) {
        warning('请至少选择一个家庭成员');
        return;
      }
    } else if (selectedClasses.length === 0 && selectedStudents.length === 0) {
      warning('请至少选择一个班级或学生');
      return;
    }

    // 检查科目一致性
    const exerciseCategories = new Set(
      exercises.filter(ex => selectedExercises.includes(ex.id)).map(ex => ex.category)
    );

    if (exerciseCategories.size > 1) {
      warning('所选习题包含不同科目，请选择同一科目的习题');
      return;
    }

    try {
      // 构建 assignments 数组
      const assignments = isParent
        ? selectedChildren.map(id => ({ type: 'student' as const, id }))
        : [
            ...selectedClasses.map(id => ({ type: 'class' as const, id })),
            ...selectedStudents.map(id => ({ type: 'student' as const, id })),
          ];

      setCreateFormField('selectedExerciseIds', selectedExercises);
      setCreateFormField('assignments', assignments);
      
      await createHomework();
      success('作业创建成功');
      setTimeout(() => navigate(`${basePath}/homeworks`), 1000);
    } catch (err) {
      console.error('Create homework error:', err);
      error('创建失败');
    }
  };

  const toggleExercise = (exerciseId: string) => {
    setSelectedExercises(prev =>
      prev.includes(exerciseId)
        ? prev.filter(id => id !== exerciseId)
        : [...prev, exerciseId]
    );
  };

  const toggleClass = (classId: string) => {
    setSelectedClasses(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const toggleStudent = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleChild = (childId: string) => {
    setSelectedChildren(prev =>
      prev.includes(childId)
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  return (
    <div className="create-homework-page min-h-screen bg-gray-50 p-6">
      {/* 头部 */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`${basePath}/homeworks`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          返回作业列表
        </button>

        <h1 className="text-2xl font-bold text-gray-900">创建作业</h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* 基本信息 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">基本信息</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                作业名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateFormField('name', e.target.value)}
                placeholder="请输入作业名称"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">作业描述</label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateFormField('description', e.target.value)}
                placeholder="请输入作业描述（可选）"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                截止日期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={createForm.dueDate}
                onChange={(e) => setCreateFormField('dueDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* 选择习题 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              选择习题 <span className="text-red-500">*</span>
            </h2>
            <button
              type="button"
              onClick={() => setShowExerciseSelector(!showExerciseSelector)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              {showExerciseSelector ? '收起' : '添加习题'}
            </button>
          </div>

          {selectedExercises.length === 0 ? (
            <p className="text-sm text-gray-500">尚未选择习题</p>
          ) : (
            <div className="space-y-2 mb-4">
              {exercises
                .filter(ex => selectedExercises.includes(ex.id))
                .map((exercise, index) => (
                  <div
                    key={exercise.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{exercise.title}</p>
                        <p className="text-xs text-gray-500">{getCategoryLabel(exercise.category)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleExercise(exercise.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
            </div>
          )}

          {/* 习题选择器 */}
          {showExerciseSelector && (
            <div className="border-t border-gray-200 pt-4">
              <div className="max-h-96 overflow-y-auto space-y-2">
                {exercises.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">暂无习题，请先创建习题</p>
                ) : (
                  exercises.map((exercise) => (
                    <div
                      key={exercise.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedExercises.includes(exercise.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleExercise(exercise.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{exercise.title}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {getCategoryLabel(exercise.category)}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedExercises.includes(exercise.id)}
                          onChange={() => {}}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* 布置对象 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            布置对象 <span className="text-red-500">*</span>
          </h2>

          {isParent ? (
            /* 家长模式：直接显示家庭子女 */
            <div className="space-y-2">
              {familyChildren.length === 0 ? (
                <p className="text-sm text-gray-500">暂无家庭成员，请先添加子女</p>
              ) : (
                familyChildren.map((child) => (
                  <div
                    key={child.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedChildren.includes(child.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleChild(child.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {child.avatar_url ? (
                          <img src={buildStaticUrl(child.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {child.nickname?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <p className="text-sm font-medium text-gray-900">{child.nickname}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedChildren.includes(child.id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </div>
                  </div>
                ))
              )}

              {/* 已选择的子女 */}
              {selectedChildren.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">已选择:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedChildren.map((childId) => {
                      const child = familyChildren.find(c => c.id === childId);
                      return child ? (
                        <span
                          key={childId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          {child.nickname}
                          <button
                            type="button"
                            onClick={() => toggleChild(childId)}
                            className="hover:text-blue-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* 老师模式：班级/学生选择 */
            <>
              {/* 切换按钮 */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setAssignmentType('class')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    assignmentType === 'class'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  按班级
                </button>
                <button
                  type="button"
                  onClick={() => setAssignmentType('student')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    assignmentType === 'student'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  按学生
                </button>
              </div>

              {/* 班级选择 */}
              {assignmentType === 'class' && (
                <div className="space-y-2">
                  {classes.length === 0 ? (
                    <p className="text-sm text-gray-500">暂无班级</p>
                  ) : (
                    classes.map((cls) => (
                      <div
                        key={cls.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedClasses.includes(cls.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleClass(cls.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{cls.name}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {cls.memberCount || 0} 名学生
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedClasses.includes(cls.id)}
                            onChange={() => {}}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* 学生选择 */}
              {assignmentType === 'student' && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {students.length === 0 ? (
                    <p className="text-sm text-gray-500">暂无学生</p>
                  ) : (
                    students.map((student) => (
                      <div
                        key={student.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedStudents.includes(student.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleStudent(student.id)}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">{student.nickname}</p>
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => {}}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* 已选择的对象 */}
              {(selectedClasses.length > 0 || selectedStudents.length > 0) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">已选择:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedClasses.map((classId) => {
                      const cls = classes.find(c => c.id === classId);
                      return cls ? (
                        <span
                          key={classId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                        >
                          班级: {cls.name}
                          <button
                            type="button"
                            onClick={() => toggleClass(classId)}
                            className="hover:text-purple-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                    {selectedStudents.map((studentId) => {
                      const student = students.find(s => s.id === studentId);
                      return student ? (
                        <span
                          key={studentId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          学生: {student.nickname}
                          <button
                            type="button"
                            onClick={() => toggleStudent(studentId)}
                            className="hover:text-blue-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={
              loading ||
              selectedExercises.length === 0 ||
              (isParent
                ? selectedChildren.length === 0
                : selectedClasses.length === 0 && selectedStudents.length === 0)
            }
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? '创建中...' : '创建作业'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`${basePath}/homeworks`)}
            className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            取消
          </button>
        </div>
      </form>

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
